
import React from 'react';
import { CartItem } from '../types';
import { formatCurrency } from '../constants';

interface CartProps {
  items: CartItem[];
  onUpdateQuantity: (id: string, delta: number, index?: number) => void;
  onCheckout: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export const Cart: React.FC<CartProps> = ({ items, onUpdateQuantity, onCheckout, isOpen, onClose }) => {
  const total = items.reduce((acc, item) => {
    const addPrice = item.adicionais_selecionados?.reduce((a, ad) => a + ad.preco, 0) || 0;
    return acc + ((item.preco + addPrice) * item.quantidade);
  }, 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] overflow-hidden">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}></div>
      
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col transform transition-transform animate-slide-in">
        <div className="px-8 py-7 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
          <h2 className="text-2xl font-black flex items-center gap-3 text-slate-900 tracking-tight">Meu Carrinho</h2>
          <button onClick={onClose} className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-full transition-all">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6 custom-scrollbar">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-6">
              <div className="w-24 h-24 bg-slate-50 rounded-[2.5rem] flex items-center justify-center border-2 border-dashed border-slate-100">
                <svg className="h-10 w-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
              </div>
              <p className="font-bold text-lg">Nada por aqui ainda...</p>
            </div>
          ) : (
            <div className="space-y-8">
              {items.map((item, index) => {
                const addPrice = item.adicionais_selecionados?.reduce((a, ad) => a + ad.preco, 0) || 0;
                const unitPriceTotal = item.preco + addPrice;
                
                return (
                  <div key={`${item.id}-${index}`} className="flex flex-col gap-2 group">
                    <div className="flex gap-5">
                      <div className="h-20 w-20 bg-slate-100 rounded-[1.5rem] overflow-hidden shrink-0 shadow-inner">
                        {item.imagem_url ? <img src={item.imagem_url} alt={item.nome} className="h-full w-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300 uppercase text-[8px] font-black">Item</div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-extrabold text-slate-900 leading-tight mb-1">{item.nome}</h3>
                        <p className="text-sm font-bold text-slate-400 mb-3">{formatCurrency(unitPriceTotal)}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 bg-slate-50 rounded-xl p-1 border border-slate-100">
                            <button onClick={() => onUpdateQuantity(item.id, -1, index)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-orange-600 font-black">-</button>
                            <span className="text-xs font-black text-slate-900 min-w-[20px] text-center">{item.quantidade}</span>
                            <button onClick={() => onUpdateQuantity(item.id, 1, index)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-orange-600 font-black">+</button>
                          </div>
                          <span className="font-black text-slate-900">{formatCurrency(unitPriceTotal * item.quantidade)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {item.adicionais_selecionados && item.adicionais_selecionados.length > 0 && (
                      <div className="ml-20 bg-slate-50 p-2.5 rounded-xl border border-slate-100 space-y-1">
                         <div className="flex flex-wrap gap-1">
                            {item.adicionais_selecionados.map((ad, idx) => (
                               <span key={idx} className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-lg text-slate-600 font-bold">
                                 {ad.nome}
                               </span>
                            ))}
                         </div>
                      </div>
                    )}

                    {item.observacao && (
                      <div className="ml-20 bg-orange-50/50 p-2.5 rounded-xl border border-orange-100/50">
                        <p className="text-xs font-medium text-slate-600 italic">"{item.observacao}"</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="px-8 py-8 border-t border-slate-100 bg-slate-50/50 space-y-6">
            <div className="flex justify-between items-center">
              <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Total do Carrinho</span>
              <span className="text-3xl font-black text-slate-900 tracking-tight">{formatCurrency(total)}</span>
            </div>
            <button onClick={onCheckout} className="w-full bg-orange-600 text-white font-black py-5 rounded-[2rem] hover:bg-orange-700 transition-all shadow-2xl active:scale-[0.98]">Ir para Pagamento</button>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slide-in 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
};
